import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link2, Copy, X, CheckCircle2, Clock, Ban, AlertCircle, Timer, Mail } from 'lucide-react';
import FeatureGate from '../../components/FeatureGate';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
// useConfirm API: confirm(message, { title?, confirmText?, type? }) → Promise<boolean>
import { useCurrency } from '../../contexts/CurrencyContext';
import useCheckoutLinkActions from './useCheckoutLinkActions';
import WhatsAppIcon from './WhatsAppIcon';
import './CheckoutLinksTab.css';

/* ─── Formatting helpers (module scope) ────────────────────────── */

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return formatDate(iso);
};

/** Forward countdown to an ISO future timestamp. Returns null when
 *  no expiry is set; the row hides the chip in that case. */
const formatCountdown = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { label: 'Expired', tone: 'past' };
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) {
    return { label: `${minutes}m left`, tone: 'urgent' };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const tone = hours < 6 ? 'urgent' : 'soon';
    return { label: `${hours}h left`, tone };
  }
  const days = Math.floor(hours / 24);
  const tone = days <= 1 ? 'soon' : 'normal';
  return { label: `${days}d left`, tone };
};

const cartPreview = (cart) => {
  if (!Array.isArray(cart) || cart.length === 0) return '—';
  const first = cart[0];
  const head = `${first.nameAtSnapshot || 'Item'} ×${Number(first.qty || 0)}`;
  const more = cart.length - 1;
  return more > 0 ? `${head} + ${more} more` : head;
};

const cartTotal = (cart) =>
  Array.isArray(cart)
    ? cart.reduce(
        (sum, it) =>
          sum + parseFloat(it.priceAtSnapshot || 0) * Number(it.qty || 0),
        0
      )
    : 0;

const STATUS_META = {
  ACTIVE:    { label: 'Active',    icon: Clock,        cls: 'sl-status-active' },
  USED:      { label: 'Used',      icon: CheckCircle2, cls: 'sl-status-used' },
  EXPIRED:   { label: 'Expired',   icon: AlertCircle,  cls: 'sl-status-expired' },
  CANCELLED: { label: 'Cancelled', icon: Ban,          cls: 'sl-status-cancelled' },
};

/* ─── Module-scope sub-components ─────────────────────────────── */

function ConversionSummaryCard({ links, formatMoney }) {
  const sent = links.length;
  const viewed = links.filter((l) => (l.viewCount || 0) > 0).length;
  const used = links.filter((l) => l.status === 'USED').length;
  const conv = sent > 0 ? Math.round((used / sent) * 100) : 0;

  return (
    <section className="sl-summary" aria-label="Conversion summary">
      <div className="sl-summary-item">
        <p className="sl-summary-label">Sent</p>
        <p className="sl-summary-value">{sent}</p>
      </div>
      <div className="sl-summary-item">
        <p className="sl-summary-label">Viewed</p>
        <p className="sl-summary-value">{viewed}</p>
      </div>
      <div className="sl-summary-item">
        <p className="sl-summary-label">Used</p>
        <p className="sl-summary-value">{used}</p>
      </div>
      <div className="sl-summary-item">
        <p className="sl-summary-label">Conversion</p>
        <p className="sl-summary-value">{conv}%</p>
      </div>
    </section>
  );
}

function StatusBadge({ status, createdOrder, formatMoney }) {
  const meta = STATUS_META[status] || STATUS_META.ACTIVE;
  const Icon = meta.icon;
  if (status === 'USED' && createdOrder) {
    return (
      <span className={`sl-status ${meta.cls}`}>
        <Icon size={14} />
        <span>
          {formatMoney(createdOrder.total)} order
        </span>
      </span>
    );
  }
  return (
    <span className={`sl-status ${meta.cls}`}>
      <Icon size={14} />
      <span>{meta.label}</span>
    </span>
  );
}

function LinkListRow({ link, onCopy, onEmail, onWhatsApp, onCancel, formatMoney }) {
  const hasCustomer = !!link.customerInfo?.name;
  const customerSub = link.customerInfo?.email || link.customerInfo?.phone || null;
  const canCancel = link.status === 'ACTIVE';
  // Only show countdown for links that can still be used — USED /
  // CANCELLED / already-EXPIRED rows have nothing to count down to.
  const countdown = link.status === 'ACTIVE' ? formatCountdown(link.expiresAt) : null;

  return (
    <article className="sl-row">
      <div className="sl-row-head">
        <p className="sl-row-title">{cartPreview(link.cart)}</p>
        <div className="sl-row-badges">
          {countdown && (
            <span className={`sl-countdown sl-countdown--${countdown.tone}`}>
              <Timer size={12} />
              <span>{countdown.label}</span>
            </span>
          )}
          <StatusBadge
            status={link.status}
            createdOrder={link.createdOrder}
            formatMoney={formatMoney}
          />
        </div>
      </div>

      {hasCustomer && (
        <p className="sl-row-customer-line">
          <span className="sl-row-customer-name">{link.customerInfo.name}</span>
          {customerSub && (
            <>
              <span className="sl-row-dot">·</span>
              <span className="sl-row-customer-sub">{customerSub}</span>
            </>
          )}
        </p>
      )}

      <div className="sl-row-meta">
        <span className="sl-row-total">{formatMoney(cartTotal(link.cart))}</span>
        <span className="sl-row-dot">·</span>
        <span>{link.viewCount || 0} view{link.viewCount === 1 ? '' : 's'}</span>
        <span className="sl-row-dot">·</span>
        <span>{formatRelative(link.createdAt)}</span>
      </div>

      <div className="sl-row-actions">
        <button
          type="button"
          className="sl-row-action"
          onClick={() => onCopy(link)}
        >
          <Copy size={14} />
          <span>Copy URL</span>
        </button>
        {canCancel && link.customerInfo?.email && (
          <button
            type="button"
            className="sl-row-action"
            onClick={() => onEmail(link)}
          >
            <Mail size={14} />
            <span>Email</span>
          </button>
        )}
        {canCancel && link.customerInfo?.phone && (
          <button
            type="button"
            className="sl-row-action sl-row-action--whatsapp"
            onClick={() => onWhatsApp(link)}
          >
            <WhatsAppIcon size={14} />
            <span>WhatsApp</span>
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            className="sl-row-action sl-row-action--danger"
            onClick={() => onCancel(link)}
          >
            <X size={14} />
            <span>Cancel</span>
          </button>
        )}
      </div>
    </article>
  );
}

function EmptyState({ onUpgradeOrSend }) {
  return (
    <div className="sl-empty">
      <Link2 size={36} className="sl-empty-icon" />
      <h2 className="sl-empty-title">No checkout links yet</h2>
      <p className="sl-empty-sub">
        Build a cart for any customer and share the link via copy, email, or WhatsApp.
      </p>
      <button
        type="button"
        className="sl-empty-cta"
        onClick={onUpgradeOrSend}
      >
        Send your first checkout link
      </button>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="sl-skeleton-list">
      {[0, 1, 2].map((i) => (
        <div key={i} className="sl-skeleton-row">
          <div className="sl-skeleton-line sl-skeleton-line--wide" />
          <div className="sl-skeleton-line sl-skeleton-line--narrow" />
          <div className="sl-skeleton-line sl-skeleton-line--mid" />
        </div>
      ))}
    </div>
  );
}

function UpgradeInline() {
  return (
    <div className="sl-upgrade">
      <Link2 size={36} className="sl-upgrade-icon" />
      <h2 className="sl-upgrade-title">Checkout links are a PRO feature</h2>
      <p className="sl-upgrade-sub">
        Pre-build a cart for any customer, share it in one tap, and track who opens and converts —
        upgrade to unlock checkout link analytics.
      </p>
      <a href="/upgrade" className="sl-upgrade-cta">View plans</a>
    </div>
  );
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function CheckoutLinksTab({ onOpenSend }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const { emailLink } = useCheckoutLinkActions();

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tick every 60s so the per-row countdown chips ("3h left", "1d left")
  // stay current without a full refetch. We don't display second-level
  // precision so a minute is plenty.
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const formatMoney = useCallback(
    (n) => formatInDisplayCurrency(Number(n || 0), accountCurrency),
    [formatInDisplayCurrency, accountCurrency]
  );

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/merchant/checkout-links?limit=100', {
        credentials: 'include',
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.error || 'Failed to load checkout links');
        setLinks([]);
      } else {
        setLinks(Array.isArray(data.links) ? data.links : []);
      }
    } catch (e) {
      setError('Network error');
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const onCopy = useCallback(
    async (link) => {
      if (!link.url) {
        toast.error('No URL available for this link');
        return;
      }
      try {
        await navigator.clipboard.writeText(link.url);
        toast.success('Link copied to clipboard');
      } catch {
        toast.error('Could not copy — please copy manually');
      }
    },
    [toast]
  );

  const onEmail = useCallback(
    async (link) => {
      const to = link.customerInfo?.email;
      if (!to) {
        toast.error('No customer email on this link');
        return;
      }
      const updated = await emailLink(link.id, to);
      if (updated) {
        setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, ...updated } : l)));
      }
    },
    [emailLink, toast]
  );

  const onWhatsApp = useCallback(
    (link) => {
      const digits = String(link.customerInfo?.phone || '').replace(/\D+/g, '');
      if (!digits || !link.url) {
        toast.error('No phone number on this link');
        return;
      }
      const greeting = 'Your checkout link: ';
      const url = `https://wa.me/${digits}?text=${encodeURIComponent(greeting + link.url)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Opening WhatsApp…');
    },
    [toast]
  );

  const onCancel = useCallback(
    async (link) => {
      const ok = await confirm(
        'Once cancelled, the customer will see a "no longer valid" page if they open it. This cannot be undone.',
        {
          title: 'Cancel this checkout link?',
          confirmText: 'Cancel link',
          cancelText: 'Keep link',
          type: 'danger',
        }
      );
      if (!ok) return;

      try {
        const resp = await fetch(
          `/api/merchant/checkout-links/${encodeURIComponent(link.id)}/cancel`,
          { method: 'POST', credentials: 'include' }
        );
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          toast.error(data.error || 'Failed to cancel link');
          return;
        }
        toast.success('Link cancelled');
        // Local update so the merchant doesn't wait for a refetch.
        setLinks((prev) =>
          prev.map((l) => (l.id === link.id ? { ...l, status: 'CANCELLED' } : l))
        );
      } catch {
        toast.error('Network error');
      }
    },
    [confirm, toast]
  );

  const fallback = useMemo(() => <UpgradeInline />, []);

  return (
    <FeatureGate featureId="sales.checkout_link" fallback={fallback}>
      <div className="sl-tab">
        {loading && <SkeletonList />}

        {!loading && error && (
          <div className="sl-error" role="alert">
            <AlertCircle size={18} />
            <span>{error}</span>
            <button type="button" className="sl-error-retry" onClick={fetchLinks}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {links.length > 0 && (
              <ConversionSummaryCard links={links} formatMoney={formatMoney} />
            )}
            {links.length === 0 ? (
              <EmptyState onUpgradeOrSend={onOpenSend} />
            ) : (
              <div className="sl-list">
                {links.map((link) => (
                  <LinkListRow
                    key={link.id}
                    link={link}
                    onCopy={onCopy}
                    onEmail={onEmail}
                    onWhatsApp={onWhatsApp}
                    onCancel={onCancel}
                    formatMoney={formatMoney}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </FeatureGate>
  );
}
